"use client"

import { useState, useEffect } from "react"
import Bet from "./Bet"
import Filters from "./Filters"
import { db } from "../firebase"
import { collection, getDocs, query, where } from "firebase/firestore"
import { useAuth } from "../context/AuthContext"
import { Users } from "lucide-react"
import { useNavigate } from "react-router-dom"

function AllBets({ onCreateBet }) {
  const navigate = useNavigate()
  const [allBets, setAllBets] = useState([])
  const [filteredBets, setFilteredBets] = useState([])
  const { currentUser, userProfile } = useAuth()
  const [friendsBets, setFriendsBets] = useState([])
  const [showFriendsBets, setShowFriendsBets] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchBets = async () => {
      if (currentUser) {
        try {
          setLoading(true)
          const uid = currentUser.uid
          const q = query(collection(db, "ActiveBets"), where("user_id", "!=", uid))
          const querySnapshot = await getDocs(q)
          const results = []

          querySnapshot.forEach((doc) => {
            const data = doc.data()
            data.id = doc.id

            var currentTime = new Date()
            var betTime = new Date(data["bet_time"])

            if (data["accepted"] === "No" && data["status"] === "Created" && betTime >= currentTime) {
              results.push(data)
            }
          })
          results.sort((a, b) => new Date(a.bet_time) - new Date(b.bet_time))
          setAllBets(results)
          setFilteredBets(results)

          // Fetch friends' bets
          const friendsBets = await fetchFriendsBets(userProfile.friends)
          setFriendsBets(friendsBets)
          setLoading(false)
        } catch (error) {
          console.error("Error fetching bets:", error)
          setLoading(false)
        }
      }
    }

    fetchBets()
  }, [currentUser, userProfile.friends])

  const fetchFriendsBets = async (friends) => {
    const finalResults = []
    const tmpFriendProfiles = []

    for (const friendEmail of friends) {
      if (friendEmail === userProfile.email) continue
      const q = query(collection(db, "users"), where("email", "==", friendEmail))
      const querySnapshot = await getDocs(q)
      for (const doc of querySnapshot.docs) {
        const friendData = doc.data()
        friendData.id = doc.id

        tmpFriendProfiles.push(friendData)

        const q2 = query(collection(db, "ActiveBets"), where("user_id", "==", doc.id))
        const querySnapshot2 = await getDocs(q2)
        querySnapshot2.forEach((doc2) => {
          const betData = doc2.data()
          betData.id = doc2.id

          var currentTime = new Date()
          var betTime = new Date(betData["bet_time"])

          if (betData["accepted"] === "No" && betData["status"] === "Created" && betTime >= currentTime) {
            finalResults.push(betData)
          }
        })
      }
    }

    finalResults.sort((a, b) => new Date(a.bet_time) - new Date(b.bet_time))
    return finalResults
  }

  const handleFilterSelect = (sportsKey, sportsTitle) => {
    if (sportsKey === "all") {
      setFilteredBets(showFriendsBets ? friendsBets : allBets)
    } else {
      const filtered = (showFriendsBets ? friendsBets : allBets).filter(
        (bet) => bet.bet_sport_key === sportsKey || bet.bet_sport_title === sportsTitle,
      )
      setFilteredBets(filtered)
    }
  }

  const handleShowFriendsBets = () => {
    setFilteredBets(showFriendsBets ? allBets : friendsBets)
  }

  return (
    <div className="min-h-screen flex flex-col items-center p-2 md:p-4">
      <div className="w-full max-w-4xl mb-4 md:mb-6 px-2 md:px-0">
        <Filters onFilterSelect={handleFilterSelect} />
      </div>
      <div className="w-full max-w-4xl bg-gray-900/30 backdrop-blur-sm p-3 md:p-6 rounded-xl border border-gray-800 shadow-lg">
        {/* Toggle button to switch between all bets and friends */}
        <div className="flex justify-center mb-6">
          <div className="inline-block rounded-full bg-gray-800/70 p-1">
            <div className="inline-flex rounded-full overflow-hidden">
              <button
                onClick={() => {
                  setShowFriendsBets(false)
                  handleShowFriendsBets()
                }}
                className={`px-6 py-2 text-sm font-medium transition-colors duration-200 
                  ${!showFriendsBets ? "bg-emerald-500 text-white" : "bg-transparent text-gray-300 hover:text-white"}`}
              >
                All Bets
              </button>
              <button
                onClick={() => {
                  setShowFriendsBets(true)
                  handleShowFriendsBets()
                }}
                className={`px-6 py-2 text-sm font-medium transition-colors duration-200 
                  ${showFriendsBets ? "bg-emerald-500 text-white" : "bg-transparent text-gray-300 hover:text-white"}`}
              >
                Friends Bets
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
            <span className="ml-3 text-white">Loading bets...</span>
          </div>
        ) : filteredBets.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {filteredBets.map((bet) => (
              <Bet key={bet.id} bet={bet} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-gray-800/70 p-4 rounded-xl inline-block mb-4">
              <Users className="w-12 h-12 text-gray-500 mx-auto" />
            </div>
            <h2 className="text-xl font-medium mb-2 text-white">No Open Bets Right Now</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              {showFriendsBets
                ? "Friend bets appear here when someone creates a wager and it is still waiting for another person to accept. Right now there are no pending friend bets in this view."
                : "All Bets shows open wagers that people created and are waiting for someone to accept. Right now there are no pending public bets in this view."}
            </p>
            <button
              type="button"
              onClick={() => {
                if (typeof onCreateBet === "function") {
                  onCreateBet()
                } else {
                  navigate("/")
                }
              }}
              className="mt-5 inline-flex items-center rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 transition-colors"
            >
              Create Bet
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default AllBets

